import { useState, useEffect, useCallback } from 'react';
import { Newspaper, Clock, ExternalLink, ChevronDown, ChevronUp, Loader as Loader2, RefreshCw } from 'lucide-react';
import { useApi } from '../../../lib/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import { sanitizeUrl } from '../../../lib/sanitizeUrl';
import type { NewsAlertResult } from '../../../types';

interface NewsItem {
  id: string;
  title: string;
  url: string;
  source: string;
  published_at: string | null;
  summary: string;
}

interface Props {
  news: NewsItem[];
  hasEnriched: boolean;
  accountId: string;
  newsFetchedAt?: string | null;
  fetchingNews?: boolean;
  onRefresh?: () => void;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

export default function RecentNewsCard({ news, hasEnriched, accountId, newsFetchedAt, fetchingNews, onRefresh }: Props) {
  const { user } = useAuth();
  const { apiGet } = useApi();
  const [alertHistory, setAlertHistory] = useState<NewsAlertResult[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // Read-only: the weekly alert subscription toggle was removed from this card because
  // nothing invokes POST /v1/weekly-news-analysis (no schedule, no caller), so switching it
  // on promised the user an email that would never arrive. Past results stay visible for any
  // account that was already subscribed.
  const loadAlerts = useCallback(async () => {
    if (!user) return;
    const { alerts } = await apiGet<{ alerts: NewsAlertResult[] }>(
      `/v1/account/${accountId}/news`,
    ).catch(() => ({ alerts: [] as NewsAlertResult[] }));
    setAlertHistory(alerts);
  }, [user, accountId, apiGet]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const generatedAlerts = alertHistory.filter(a => a.alert_generated);

  return (
    <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-app-ink flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-forest-600" /> Recent News
        </h2>

        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={fetchingNews}
            className="p-1 rounded-lg text-app-muted hover:text-forest-600 hover:bg-forest-50 disabled:text-app-line-strong disabled:hover:bg-transparent transition-colors"
            title="Refresh news"
          >
            <RefreshCw className={`w-4 h-4 ${fetchingNews ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>

      {newsFetchedAt && !fetchingNews && (
        <p className="text-[11px] text-app-muted mb-3 -mt-2">
          Live news fetched {formatDate(newsFetchedAt)}
        </p>
      )}
      {fetchingNews && (
        <div className="flex items-center gap-2 mb-3 -mt-2">
          <Loader2 className="w-3.5 h-3.5 text-app-focus animate-spin" />
          <p className="text-[11px] text-app-focus font-medium">Fetching latest news...</p>
        </div>
      )}

      {news.length === 0 ? (
        <div className="text-center py-8 flex-1 flex flex-col items-center justify-center">
          <Clock className="w-8 h-8 text-app-line-strong mx-auto mb-2" />
          <p className="text-sm text-app-muted">
            {fetchingNews ? 'Fetching news...' : hasEnriched ? 'No recent news found' : 'News will appear after enrichment'}
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1 custom-scrollbar">
          {news.map(item => (
            <a
              key={item.id}
              href={sanitizeUrl(item.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="block p-3.5 bg-app-canvas rounded-lg hover:bg-forest-50 transition-colors group"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-app-ink group-hover:text-forest-700 transition-colors leading-snug">
                  {item.title}
                </p>
                <ExternalLink className="w-3.5 h-3.5 text-app-line-strong group-hover:text-forest-500 flex-shrink-0 mt-0.5 transition-colors" />
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                {item.source && (
                  <span className="text-xs font-medium text-forest-600 bg-forest-50 px-2 py-0.5 rounded-full">
                    {item.source}
                  </span>
                )}
                {item.published_at && (
                  <span className="text-xs text-app-muted">{formatDate(item.published_at)}</span>
                )}
              </div>
              {item.summary && (
                <p className="text-xs text-app-muted mt-1.5 leading-relaxed line-clamp-2">{item.summary}</p>
              )}
            </a>
          ))}
        </div>
      )}

      {alertHistory.length > 0 && (
        <div className="mt-4 pt-4 border-t border-app-line">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-between w-full text-left"
          >
            <span className="text-xs font-semibold text-app-muted uppercase tracking-wider">
              Alert History ({generatedAlerts.length} alert{generatedAlerts.length !== 1 ? 's' : ''} sent)
            </span>
            {showHistory ? <ChevronUp className="w-4 h-4 text-app-muted" /> : <ChevronDown className="w-4 h-4 text-app-muted" />}
          </button>
          {showHistory && (
            <div className="mt-2 space-y-1.5 max-h-[200px] overflow-y-auto">
              {alertHistory.map(alert => (
                <div key={alert.id} className={`p-2.5 rounded-lg text-xs ${alert.alert_generated ? 'bg-app-critical/10' : 'bg-app-canvas'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-app-muted font-medium">{formatDate(alert.alert_date)}</span>
                    {alert.alert_generated ? (
                      <span className="text-app-critical font-semibold">Alert sent</span>
                    ) : (
                      <span className="text-app-muted">No alert</span>
                    )}
                  </div>
                  {alert.alert_generated && alert.alert_summary && (
                    <p className="text-app-muted mt-1 leading-relaxed">{alert.alert_summary}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
